import {
    SpacingToken,
    UiStack,
} from '@experiences/ui-common';
import { Box } from '@mui/material';
import {
    ApButton,
    ApTextArea,
    ApTextField,
} from '@uipath/portal-shell-react';
import React from 'react';
import { useIntl } from 'react-intl';

import { useStepHooks } from '../../../../common/UiWizard';
import {
    WizardStepContainer,
    WizardStepHeader,
} from '../WizardStepLayout';
import useGovernancePolicyCreateDetailsStepViewModel from './GovernancePolicyCreateDetailsStepViewModel';

interface GovernancePolicyCreateDetailsStepProps {
    type?: 'add' | 'edit';
    isDrawerMode?: boolean;
    onDone?: () => void;
}

/**
 * Tiny render-prop-style sibling component that calls `useStepHooks`
 * unconditionally. We render it only in wizard mode, so the hook call
 * itself stays unconditional (Rules of Hooks) while the registration is
 * gated at the JSX level — no `skip` flag required on `useStepHooks`.
 */
const WizardStepHooksRegistrar: React.FC<{ stepHooks: { disableNext?: boolean } }> = ({ stepHooks }) => {
    useStepHooks(stepHooks);
    return null;
};

export const GovernancePolicyCreateDetailsStep: React.FC<GovernancePolicyCreateDetailsStepProps> = ({
    type = 'add', isDrawerMode = false, onDone,
}) => {
    const { formatMessage: translate } = useIntl();
    const {
        name,
        description,
        handleNameChange,
        handleDescriptionChange,
        stepHooks,
    } = useGovernancePolicyCreateDetailsStepViewModel();

    const fields = (
        <>
            <ApTextField
                style={{ width: '100%' }}
                label={translate({ id: 'CLIENT_GOVERNANCE_POLICY_NAME_LABEL' })}
                value={name}
                onValueChanged={handleNameChange}
                required
                data-cy="governance-policy-name-field"
            />

            <ApTextArea
                style={{ width: '100%' }}
                label={translate({ id: 'CLIENT_GOVERNANCE_POLICY_DESCRIPTION_LABEL' })}
                value={description}
                onValueChanged={handleDescriptionChange}
                maxRows={4}
                resize="none"
                data-cy="governance-policy-description-field"
            />
        </>
    );

    if (isDrawerMode) {
        const isNameInvalid = !name || name.trim() === '';
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    overflow: 'hidden',
                }}
                data-cy="governance-policy-create-details-step"
            >
                <Box sx={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: SpacingToken.L,
                }}>
                    <UiStack
                        direction="column"
                        gap={SpacingToken.L}
                        p={SpacingToken.XL}
                    >
                        {fields}
                    </UiStack>
                </Box>
                <Box
                    sx={{
                        borderTop: '1px solid var(--color-border-de-emp)',
                        background: 'var(--color-background)',
                        padding: '12px',
                        flexShrink: 0,
                    }}
                >
                    <UiStack
                        direction="row"
                        justify="end"
                        gap={SpacingToken.M}
                        p={SpacingToken.S}
                    >
                        <ApButton
                            variant="primary"
                            onClick={onDone}
                            label={translate({ id: 'CLIENT_DONE' })}
                            disabled={isNameInvalid}
                            data-cy="governance-policy-details-done"
                        />
                    </UiStack>
                </Box>
            </Box>
        );
    }

    return (
        <WizardStepContainer type={type}>
            <WizardStepHooksRegistrar stepHooks={stepHooks} />
            <UiStack
                direction="column"
                justify="between"
                h="inherit"
                data-cy="governance-policy-create-details-step"
            >
                <UiStack
                    direction="column"
                    gap={SpacingToken.L}
                >
                    <WizardStepHeader
                        title={translate({ id: 'CLIENT_GOVERNANCE_STEP_DETAILS_TITLE' })}
                        description={translate({ id: 'CLIENT_GOVERNANCE_STEP_DETAILS_DESCRIPTION' })}
                    />
                    <Box sx={{ width: '700px' }}>
                        <UiStack
                            direction="column"
                            gap={SpacingToken.L}
                        >
                            {fields}
                        </UiStack>
                    </Box>
                </UiStack>
            </UiStack>
        </WizardStepContainer>
    );
};
